import React, { useCallback, useEffect, useState } from 'react';
import { Btn, C, Card } from '../../../components/UI';
import { platformApi, TenantTemplateDto } from '../../../services/api';
import TenantTemplateEditForm from './TenantTemplateEditForm';

const statusColors: Record<string, [string, string]> = {
    Draft: ['#F5F5F5', '#666'],
    Published: [C.greenBg, C.green],
    Archived: ['#FFF0F0', '#9B1C1C'],
};

export default function TenantTemplateCatalogAdmin() {
    const [templates, setTemplates] = useState<TenantTemplateDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<TenantTemplateDto | 'new' | null>(null);
    const [working, setWorking] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setTemplates(await platformApi.getTenantTemplates()); }
        catch (e: any) { setError(e.message || 'Failed to load templates.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const publish = async (id: string) => {
        setWorking(id);
        try { await platformApi.publishTenantTemplate(id); await load(); }
        catch (e: any) { setError(e.message || 'Failed to publish.'); }
        finally { setWorking(null); }
    };

    const archive = async (id: string) => {
        if (!window.confirm('Archive this template? It will no longer be offered when onboarding new tenants.')) return;
        setWorking(id);
        try { await platformApi.archiveTenantTemplate(id); await load(); }
        catch (e: any) { setError(e.message || 'Failed to archive.'); }
        finally { setWorking(null); }
    };

    if (editing) {
        return (
            <TenantTemplateEditForm
                template={editing === 'new' ? null : editing}
                onSaved={() => { setEditing(null); load(); }}
                onCancel={() => setEditing(null)}
            />
        );
    }

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Tenant Templates</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textSub }}>
                        Starter bundles (flags + marketplace apps + starter pricing) offered when onboarding a new tenant.
                    </p>
                </div>
                <Btn onClick={() => setEditing('new')}>+ New template</Btn>
            </div>

            {loading && <div style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>Loading templates...</div>}
            {error && <div style={{ color: '#D84040', padding: '12px 16px', background: '#FFF0F0', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

            {!loading && !error && (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {templates.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No templates yet.</div>}
                    {templates.map(t => {
                        const [bg, color] = statusColors[t.status] ?? statusColors.Draft;
                        return (
                            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
                                <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setEditing(t)}>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{t.name}</div>
                                    <div style={{ fontSize: 12, color: C.textMuted }}>
                                        {Object.keys(t.featureFlagOverrides).length} flags · {t.marketplaceAppKeys.length} apps ·{' '}
                                        {t.starterPricingRules.length + t.starterServiceCatalogItems.length} starter items
                                    </div>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: bg, color }}>{t.status}</span>
                                <Btn variant="outline" size="sm" onClick={() => setEditing(t)}>Edit</Btn>
                                {t.status !== 'Published' && (
                                    <Btn size="sm" onClick={() => publish(t.id)} disabled={working === t.id}>
                                        {working === t.id ? 'Publishing…' : 'Publish'}
                                    </Btn>
                                )}
                                {t.status !== 'Archived' && (
                                    <Btn variant="danger" size="sm" onClick={() => archive(t.id)} disabled={working === t.id}>Archive</Btn>
                                )}
                            </div>
                        );
                    })}
                </Card>
            )}
        </>
    );
}
